// Run detail side panels (design/isomorphic run-side): the status timeline, cost and
// tokens, the broker calls made for the run, and what the host can and cannot read.
import { Check, CheckCircle, Eye, EyeSlash, Prohibit, SealCheck, ShieldCheck, WarningCircle, X } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import type { Run } from "../../api/client";
import { brokerCallsQuery } from "../../dashboard/api";
import { formatNumber, useT } from "../../i18n";
import { WidgetCard } from "../../ui/Card";
import { cn } from "../../ui/cn";
import { EmptyState, Loading } from "../../ui/states";
import { Timeline } from "../../ui/Timeline";
import { clockTime, kTokens, usd } from "../format";
import { runTimeline } from "../runs";

export function RunTimelineCard({ run, status }: { run: Run; status: string }) {
  const t = useT().workloads.run;
  return (
    <WidgetCard title={t.status}>
      <Timeline steps={runTimeline(run, status)} />
    </WidgetCard>
  );
}

export function RunCostCard({ run, model }: { run: Run; model: string | undefined }) {
  const t = useT().workloads.run.cost;
  const input = run.input_tokens ?? 0;
  const output = run.output_tokens ?? 0;
  const total = input + output;
  const parts = [
    { label: t.input, value: input, color: "bg-[rgb(var(--chart-1))]" },
    { label: t.output, value: output, color: "bg-[rgb(var(--chart-2))]" },
  ];
  return (
    <WidgetCard title={t.title} description={model}>
      <div className="flex items-end justify-between">
        <div>
          <div className="tabular font-display text-3xl font-extrabold text-gray-900">{usd(run.cost_usd)}</div>
          <div className="text-xs text-gray-500">{run.finished_at == null ? t.soFar : t.inTotal} · {t.apiPrice}</div>
        </div>
        <div className="text-end">
          <div className="tabular text-lg font-bold text-gray-900">{kTokens(total)}</div>
          <div className="text-xs text-gray-500">{t.tokens}</div>
        </div>
      </div>
      {total > 0 ? (
        <div className="mt-4 flex h-2.5 gap-0.5 overflow-hidden rounded-full">
          {parts.map((p) => (
            <div key={p.label} className={cn("h-full", p.color)} style={{ width: `${(p.value / total) * 100}%`, minWidth: 6 }} />
          ))}
        </div>
      ) : null}
      <ul className="mb-0 mt-3 list-none space-y-1.5 p-0 text-[13px]">
        {parts.map((p) => (
          <li key={p.label} className="flex items-center gap-2 text-gray-600">
            <span className={cn("h-2.5 w-2.5 rounded-sm", p.color)} />
            {p.label}
            <span className="tabular ms-auto font-medium text-gray-900">{formatNumber(p.value)}</span>
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}

const ICON: Record<string, ReactNode> = {
  allowed: <CheckCircle weight="fill" className="h-4 w-4 text-green" />,
  approved: <SealCheck weight="fill" className="h-4 w-4 text-primary" />,
  denied: <Prohibit weight="fill" className="h-4 w-4 text-red" />,
};

export function RunBrokerCallsCard({ run }: { run: Run }) {
  const t = useT().workloads.run.broker;
  const query = useQuery(brokerCallsQuery(run.org_id));
  const calls = (query.data ?? []).filter((c) => c.run_id === run.id);
  return (
    <WidgetCard title={t.title} description={t.description}>
      {query.isPending ? <Loading /> : null}
      {!query.isPending && calls.length === 0 ? (
        <EmptyState compact icon={<ShieldCheck weight="duotone" />} title={t.emptyTitle}>
          {query.isError ? t.notReported : t.noneRecent}
        </EmptyState>
      ) : null}
      <ul className="m-0 list-none space-y-3 p-0">
        {calls.map((c) => (
          <li key={`${c.at}-${c.tool}-${c.action}`} className="flex items-start gap-2.5">
            <span className="mt-0.5">{ICON[c.outcome] ?? <WarningCircle weight="fill" className="h-4 w-4 text-gray-400" />}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-mono text-[12.5px] font-medium text-gray-900">
                {c.tool}.{c.action}
              </div>
              <div className="truncate text-xs text-gray-500">
                {[c.resource, c.credential_name].filter(Boolean).join(" · ") || c.outcome}
              </div>
            </div>
            <span className="tabular shrink-0 font-mono text-[11px] text-gray-500">{clockTime(c.at)}</span>
          </li>
        ))}
      </ul>
    </WidgetCard>
  );
}

export function HostVisibilityCard() {
  const t = useT().workloads.run.host;
  return (
    <WidgetCard title={t.title}>
      <div className="space-y-4">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-gray-500">
            <Eye weight="duotone" className="h-4 w-4" /> {t.metadata}
          </div>
          <ul className="m-0 list-none space-y-1.5 p-0">
            {t.can.map((line) => (
              <li key={line} className="flex gap-2 text-[13px] text-gray-700">
                <Check weight="bold" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                {line}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg bg-secondary-lighter/60 p-3.5">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-secondary-dark">
            <EyeSlash weight="duotone" className="h-4 w-4" /> {t.onlyDevices}
          </div>
          <ul className="m-0 list-none space-y-1.5 p-0">
            {t.cannot.map((line) => (
              <li key={line} className="flex gap-2 text-[13px] text-gray-800">
                <X weight="bold" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-secondary-dark" />
                {line}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </WidgetCard>
  );
}
